import axios from "axios";
import toast from "react-hot-toast";

export const getApiErrorMessage = (error, fallback = "An error occurred") =>
  error?.response?.data?.message || error?.message || fallback;

const isPublicBrowsingPath = () => {
  const path = window.location.pathname;
  return (
    path === "/" ||
    path.startsWith("/shop") ||
    path.startsWith("/products") ||
    path.startsWith("/cart") ||
    path.startsWith("/checkout") ||
    path.startsWith("/track") ||
    path.startsWith("/wishlist") ||
    path.startsWith("/contact") ||
    path.startsWith("/faq")
  );
};
export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.knotsandkrafts.in/api";

/** Canonical site URL for canonicals, og:url, and absolute links. Set REACT_APP_SITE_URL in .env to override. */
export const SITE_BASE_URL =
  process.env.REACT_APP_SITE_URL || "https://knotsandkrafts.in";

// Create axios instance with enhanced error handling
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased timeout for file uploads
  headers: {
    "Content-Type": "application/json",
  },
  // Add retry configuration
  retry: 3,
  retryDelay: (retryCount) => {
    return retryCount * 1000; // Progressive delay
  },
});

// Request interceptor to add auth token and handle retries
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Handle FormData uploads
    if (config.data instanceof FormData) {
      // Remove Content-Type header to let browser set it with boundary
      delete config.headers["Content-Type"];
      // Increase timeout for uploads
      config.timeout = 300000; // 5 minutes
    }

    // Add request ID for tracking
    config.headers["X-Request-ID"] = `req-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 10)}`;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor with enhanced error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors and timeouts
    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      console.warn("Request timeout, retrying...", originalRequest.url);

      if (!originalRequest._retry && originalRequest.retry > 0) {
        originalRequest._retry = true;
        originalRequest.retry--;

        // Wait before retry
        await new Promise((resolve) =>
          setTimeout(
            resolve,
            originalRequest.retryDelay(3 - originalRequest.retry),
          ),
        );

        return api(originalRequest);
      }
    }

    // Handle connection errors
    if (
      error.message.includes("Network Error") ||
      error.message.includes("ERR_NETWORK")
    ) {
      console.warn("Network error, retrying...", originalRequest.url);

      if (!originalRequest._retry && originalRequest.retry > 0) {
        originalRequest._retry = true;
        originalRequest.retry--;

        // Wait before retry
        await new Promise((resolve) =>
          setTimeout(
            resolve,
            originalRequest.retryDelay(3 - originalRequest.retry),
          ),
        );

        return api(originalRequest);
      }
    }

    // Handle specific HTTP errors
    if (error.response) {
      const { status, data } = error.response;
      const skipToast = originalRequest?.skipGlobalErrorToast;

      switch (status) {
        case 401:
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          if (!originalRequest?.skipAuthRedirect && !isPublicBrowsingPath()) {
            window.location.href = "/login";
          }
          break;
        case 403:
          if (!skipToast) {
            toast.error(
              "Access denied. You don't have permission for this action.",
            );
          }
          break;
        case 404:
          break;
        case 429:
          if (!skipToast) {
            toast.error(
              "Too many requests. Please wait a moment and try again.",
            );
          }
          break;
        case 500:
          if (!skipToast) {
            toast.error("Server error. Please try again later.");
          }
          break;
        default:
          if (!skipToast) {
            if (data?.message) {
              toast.error(data.message);
            } else {
              toast.error("An error occurred. Please try again.");
            }
          }
      }
    } else if (error.request) {
      if (!originalRequest?.skipGlobalErrorToast) {
        console.error("Network error:", error.message);
        toast.error(
          "Network error. Please check your connection and try again.",
        );
      }
    } else {
      if (!originalRequest?.skipGlobalErrorToast) {
        console.error("Request error:", error.message);
        toast.error("An error occurred. Please try again.");
      }
    }

    return Promise.reject(error);
  },
);

// Enhanced API request function with better error handling
export const apiRequest = async (url, options = {}) => {
  const { skipGlobalErrorToast, ...axiosOptions } = options;

  try {
    const response = await api({
      url,
      skipGlobalErrorToast,
      ...axiosOptions,
    });
    return response.data;
  } catch (error) {
    // Log error for debugging
    console.error(`API Request failed for ${url}:`, error);

    // Re-throw the error for components to handle
    throw error;
  }
};

// Helper function for GET requests
export const apiGet = (url, config = {}) => {
  return apiRequest(url, { method: "GET", ...config });
};

// Helper function for POST requests
export const apiPost = (url, data = {}, config = {}) => {
  return apiRequest(url, { method: "POST", data, ...config });
};

// Helper function for PUT requests
export const apiPut = (url, data = {}, config = {}) => {
  return apiRequest(url, { method: "PUT", data, ...config });
};

// Helper function for DELETE requests
export const apiDelete = (url, config = {}) => {
  return apiRequest(url, { method: "DELETE", ...config });
};

// Specialized function for file uploads
export const uploadRequest = async (
  url,
  formData,
  onProgress = null,
  method = "POST",
) => {
  try {
    const requestMethod = method;

    const config = {
      url,
      method: requestMethod,
      data: formData,
      timeout: 300000, // 5 minutes for uploads
      headers: {
        // Don't set Content-Type for FormData - let browser handle it
      },
    };

    // Add progress tracking if provided
    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress(percentCompleted);
      };
    }

    const response = await api(config);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Upload failed:",
      error.response?.status,
      error.response?.data || error.message,
    );
    throw error;
  }
};

// Order API functions
export const submitCheckout = async (checkoutData) => {
  const response = await api.post("/checkout", checkoutData);
  return response.data;
};

export const checkCheckoutEmail = async (email) => {
  const response = await api.get("/checkout/check-email", {
    params: { email },
    skipGlobalErrorToast: true,
  });
  return response.data;
};

export const submitContact = async (contactData) => {
  const response = await api.post("/contact", contactData, {
    skipGlobalErrorToast: true,
  });
  return response.data;
};

export const verifyCheckoutPayment = async (paymentData) => {
  const response = await api.post("/checkout/verify", paymentData);
  return response.data;
};

export const cancelCheckout = async ({ orderToken, orderId } = {}) => {
  const response = await api.post(
    "/checkout/cancel",
    { orderToken, orderId },
    { skipGlobalErrorToast: true },
  );
  return response.data;
};

export const trackOrder = async (token) => {
  const encoded = encodeURIComponent((token || "").trim());
  const response = await api.get(`/orders/track/${encoded}`, {
    skipGlobalErrorToast: true,
  });
  return response.data;
};

export const validateCoupon = async (code, orderItems) => {
  try {
    const response = await api.post("/coupons/validate", { code, orderItems });
    const body = response.data;

    if (body?.success && body?.data) {
      const d = body.data;
      const discountAmount = Number(d.discountAmount) || 0;
      const itemsPrice = Number(d.itemsPrice) || 0;

      return {
        valid: true,
        message: d.couponCode
          ? `Coupon ${d.couponCode} applied`
          : "Coupon applied",
        couponCode: d.couponCode,
        discountAmount,
        itemsPrice,
        itemsAfterDiscount:
          Math.round((itemsPrice - discountAmount) * 100) / 100,
        shippingPrice: d.shippingPrice,
        taxPrice: d.taxPrice,
        totalPrice: d.totalPrice,
      };
    }

    return { valid: false, message: body?.message || "Invalid coupon" };
  } catch (error) {
    return {
      valid: false,
      message:
        error.response?.data?.message ||
        error.message ||
        "Could not validate coupon",
    };
  }
};

export const getAdminCoupons = async (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") searchParams.set(k, v);
  });
  const response = await api.get(`/admin/coupons?${searchParams}`);
  return response.data;
};

export const createCoupon = async (data) => {
  const response = await api.post("/admin/coupons", data);
  return response.data;
};

export const updateCoupon = async (id, data) => {
  const response = await api.put(`/admin/coupons/${id}`, data);
  return response.data;
};

export const deactivateCoupon = async (id) => {
  const response = await api.patch(`/admin/coupons/${id}/deactivate`);
  return response.data;
};

export const getCouponUsage = async (id) => {
  const response = await api.get(`/admin/coupons/${id}/usage`);
  return response.data;
};

// Legacy — kept for admin recovery scripts; checkout uses submitCheckout + verifyCheckoutPayment
export const createGuestAccount = async (accountData) => {
  try {
    const response = await api.post("/guest/create-account", accountData);
    return response.data;
  } catch (error) {
    console.error("Guest account creation error:", error);
    throw error;
  }
};

// Legacy — deprecated; checkout uses submitCheckout + verifyCheckoutPayment

export const getOrder = async (orderId) => {
  const response = await api.get(`/orders/${orderId}`);
  return response.data;
};

export const getUserOrders = async (page = 1, limit = 10) => {
  const response = await api.get(`/orders/user?page=${page}&limit=${limit}`);
  return response.data;
};

// Address API functions
export const getUserAddresses = async () => {
  const response = await api.get("/addresses");
  return response.data;
};

export const createAddress = async (addressData) => {
  const response = await api.post("/addresses", addressData);
  return response.data;
};

export const updateAddress = async (addressId, addressData) => {
  const response = await api.put(`/addresses/${addressId}`, addressData);
  return response.data;
};

export const deleteAddress = async (addressId) => {
  const response = await api.delete(`/addresses/${addressId}`);
  return response.data;
};

export const setDefaultAddress = async (addressId) => {
  const response = await api.put(`/addresses/${addressId}/default`);
  return response.data;
};

// Admin Orders API functions
export const getAdminOrders = async (params) => {
  const response = await api.get("/admin/orders", { params });
  return response.data;
};

export const getAdminOrder = async (orderId) => {
  const response = await api.get(`/admin/orders/${orderId}`);
  return response.data;
};

export const updateOrderStatus = async (orderId, status) => {
  const response = await api.put(`/admin/orders/${orderId}/status`, { status });
  return response.data;
};

export const updatePaymentStatus = async (orderId, payment_status) => {
  const response = await api.put(`/admin/orders/${orderId}/payment-status`, {
    payment_status,
  });
  return response.data;
};

export const updateOrderShipping = async (orderId, shippingData) => {
  const response = await api.put(
    `/admin/orders/${orderId}/shipping`,
    shippingData,
  );
  return response.data;
};

export const exportOrders = async (params) => {
  const response = await api.get("/admin/orders/export", {
    params,
    responseType: "blob",
  });
  return response.data;
};

export const getOrderStats = async () => {
  const response = await api.get("/admin/orders/stats");
  return response.data;
};

export const getSalesAnalytics = async (period = "30") => {
  const response = await api.get(`/admin/sales/analytics?period=${period}`);
  return response.data;
};

// Newsletter API
export const getNewsletterRecipients = async (limit = 2000) => {
  const response = await api.get("/admin/newsletter/recipients", {
    params: { limit },
  });
  return response.data;
};

export const getNewsletterProductsTrending = async (limit = 12) => {
  const response = await api.get("/admin/newsletter/products/trending", {
    params: { limit },
  });
  return response.data;
};

export const getNewsletterProductsLatest = async (limit = 12) => {
  const response = await api.get("/admin/newsletter/products/latest", {
    params: { limit },
  });
  return response.data;
};

export const uploadNewsletterImage = async (formData) => {
  const response = await api.post("/admin/newsletter/upload-image", formData, {
    timeout: 60000,
  });
  return response.data;
};

export const previewNewsletter = async (payload) => {
  const response = await api.post("/admin/newsletter/preview", payload);
  return response.data;
};

export const sendNewsletter = async (payload) => {
  const response = await api.post("/admin/newsletter/send", payload);
  return response.data;
};

// Lost Orders / Payments API
export const getLostPaymentStats = async () => {
  const response = await api.get("/admin/lost-payments/stats");
  return response.data?.data;
};

export const getLostPayments = async (params) => {
  const response = await api.get("/admin/lost-payments", { params });
  return response.data?.data;
};

export const lookupLostPayment = async (paymentId) => {
  const response = await api.get(
    `/admin/lost-payments/lookup/${encodeURIComponent(paymentId)}`,
  );
  return response.data?.data;
};

export const recoverLostPayment = async (payload) => {
  const response = await api.post("/admin/lost-payments/recover", payload);
  return response.data?.data;
};

export const dismissLostPayment = async (id, adminNotes) => {
  const response = await api.post(`/admin/lost-payments/${id}/dismiss`, {
    admin_notes: adminNotes,
  });
  return response.data?.data;
};

export const getNeedsAttentionOrders = async () => {
  const response = await api.get("/admin/lost-payments/needs-attention");
  return response.data?.data;
};

export const resendOrderConfirmationEmail = async (orderId) => {
  const response = await api.post(
    `/admin/lost-payments/resend-email/${orderId}`,
  );
  return response.data?.data;
};

export const importLostPaymentsFromLogs = async () => {
  const response = await api.post("/admin/lost-payments/import-from-logs");
  return response.data?.data;
};

export default api;
